const principles = [
  {
    title: 'Clean Architecture',
    description: 'Modular, testable, and maintainable codebases designed for long-term evolution and adaptability. Every component is structured with clear separation of concerns.',
    accent: 'primary' as const,
  },
  {
    title: 'Performance Optimization',
    description: 'Systems engineered for speed, efficiency, and responsiveness at every layer. From memory management to rendering pipelines, every detail is tuned.',
    accent: 'accent' as const,
  },
  {
    title: 'User-Centered Design',
    description: 'Interfaces crafted with precision, accessibility, and intuitive interaction in mind. The end user experience drives every technical decision we make.',
    accent: 'secondary' as const,
  },
  {
    title: 'Scalable Systems',
    description: 'Architectures built to grow seamlessly with increasing demands and complexity, ensuring long-term reliability without sacrificing performance.',
    accent: 'primary' as const,
  },
  {
    title: 'Technical Excellence',
    description: 'Rigorous quality standards and modern engineering practices applied consistently across all projects to ensure reliability and maintainability.',
    accent: 'accent' as const,
  },
  {
    title: 'Continuous Innovation',
    description: 'Active exploration of emerging technologies and constant evolution of development workflows to stay at the forefront of software engineering.',
    accent: 'secondary' as const,
  },
];

const accentBarColors: Record<string, string> = {
  primary: 'oklch(var(--primary-500))',
  accent: 'oklch(var(--accent-500))',
  secondary: 'oklch(var(--secondary-500))',
};

const accentDotColors: Record<string, string> = {
  primary: 'oklch(var(--primary-100))',
  accent: 'oklch(var(--accent-100))',
  secondary: 'oklch(var(--secondary-100))',
};

export default function PrinciplesSection() {
  return (
    <section className="w-full py-20 md:py-28 px-4 md:px-6" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
      <div className="max-w-5xl mx-auto">
        <div className="mb-4">
          <span
            className="text-xs md:text-sm font-medium tracking-widest uppercase"
            style={{ fontFamily: 'var(--font-label)', color: 'oklch(var(--foreground-500))' }}
          >
            Engineering Principles
          </span>
        </div>

        <h2
          className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold mb-10 md:mb-14"
          style={{ color: 'oklch(var(--foreground-900))' }}
        >
          How we build software
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {principles.map((principle, index) => (
            <div
              key={principle.title}
              className="group rounded-lg p-6 md:p-8 relative overflow-hidden"
              style={{ backgroundColor: 'oklch(var(--background-100))' }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-1 rounded-t-lg"
                style={{ backgroundColor: accentBarColors[principle.accent] }}
              />
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: accentBarColors[principle.accent] }}
                />
                <span
                  className="text-xs font-medium tracking-widest uppercase"
                  style={{ fontFamily: 'var(--font-label)', color: 'oklch(var(--foreground-400))' }}
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
              </div>
              <h3
                className="text-lg md:text-xl font-heading font-medium mb-3"
                style={{ color: 'oklch(var(--foreground-900))' }}
              >
                {principle.title}
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{ color: 'oklch(var(--foreground-600))' }}
              >
                {principle.description}
              </p>
              <div
                className="absolute bottom-4 right-4 w-12 h-12 rounded-full flex items-center justify-center opacity-20"
                style={{ backgroundColor: accentDotColors[principle.accent] }}
              >
                <span
                  className="text-lg font-heading font-semibold"
                  style={{ color: accentBarColors[principle.accent] }}
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}